import React from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { Menu } from 'components/navigation';
import { SearchFieldDesktop } from 'components/search';
import { SnowfallToggle } from 'components/seasonal';
import { Logo } from 'components/ui';
import { getDesktopNavigationItems } from 'constants/navigation';
import { useUser } from 'hooks/data/useUser';
import { useIsOpen } from 'hooks/useIsOpen';

import HeaderCartButton from '../HeaderCartButton';

import DeliveryButtons from './DeliveryButtonsDesktop';
import HeaderCategoriesNavigation from './HeaderCategoriesNavigation';
import MegaMenuDesktop from './MegaMenuDesktop/MegaMenuDesktop';
import UserPopoverDesktop from './UserPopoverDesktop';
import UsualProductsPopoverDesktop from './UsualProductsPopoverDesktop';

import styles from './HeaderDesktop.module.scss';

interface HeaderDesktopProps {
  withCategoriesBar?: boolean,
  hideCart?: boolean,
  hideCartOnLargeScreens?: boolean,
  hideDeliveryDetails?: boolean
}

const HeaderDesktop = (props: HeaderDesktopProps) => {

  const {
    withCategoriesBar,
    ...rest
  } = props;

  // Render

  return (
    <div className={styles.root} data-testid="header-desktop">

      {/* Main */}
      <MainBar />

      {/* Sub */}
      <SubBar {...rest} />

      {/* Categories */}
      {
        withCategoriesBar && (
          <Bar className={styles.categories}>
            <HeaderCategoriesNavigation />
          </Bar>
        )
      }

    </div>
  );
};

// Components

// --- Main

const MainBar = () => {

  // Data Hooks

  const { data: user } = useUser({ isOptional: true });

  // Render

  return (
    <Bar className={styles.main}>

      {/* Logo */}
      <div className={styles.logoContainer}>
        <Logo width={200} height={50} hasHolidayIcon />
        <SnowfallToggle />
      </div>

      {/* Search Field */}
      <div className={styles.searchFieldContainer}>
        <SearchFieldDesktop />
      </div>

      {/* Links */}
      <div className={styles.mainButtons}>

        {/* Usual Products Popover */}
        <UsualProductsPopoverDesktop
          className={styles.usualProductsPopover}
          buttonClassName={styles.mainButton}
          //
          user={user}
          padding={18}
        />

        {/* User Account Menu Popover */}
        <UserPopoverDesktop
          className={styles.userPopover}
          buttonClassName={styles.mainButton}
          //
          user={user}
          padding={18}
        />

      </div>

    </Bar>
  );
};

// --- Sub

interface HeaderSubBarProps {
  hideCartOnLargeScreens?: boolean,
  hideCart?: boolean,
  hideDeliveryDetails?: boolean
}

const SubBar = (props: HeaderSubBarProps) => {

  const {
    hideCartOnLargeScreens,
    hideCart,
    hideDeliveryDetails,
  } = props;

  // Hooks

  const router = useRouter();

  const {
    isOpen: isMenuVisible, open: openMenu, close: closeMenu, toggle: toggleMenu
  } = useIsOpen();

  const { data: user } = useUser({ isOptional: true });
  const { isGeniusEligible3M10 } = user || {};

  // Handlers

  const handleRouteChange = React.useCallback(() => {
    closeMenu();
  }, [isMenuVisible]);

  // Effects

  React.useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  // Props

  const navItems = React.useMemo(() => {
    return getDesktopNavigationItems({
      shouldShowGeniusLink: !user,
      shouldShowGeniusDiscountIcon: isGeniusEligible3M10
    });
  }, [isGeniusEligible3M10]);

  // Render

  return (
    <Bar className={styles.sub}>

      {/* Mega Menu */}
      <MegaMenuDesktop
        isOpen={isMenuVisible}
        open={openMenu}
        close={closeMenu}
        toggle={toggleMenu}
      />

      {/* Navigation Menu */}
      <Menu
        className={styles.navigationMenu}
        itemClassName={styles.navigationMenuItem}
        linkWrapperClassName={styles.navigationMenuLinkWrapper}
        linkClassName={styles.navigationMenuLink}
        linkActiveClassName={styles.navigationMenuLinkActive}
        subLinkClassName={styles.navigationMenuSubLink}
        buttonClassName={styles.navigationMenuButton}
        //
        items={navItems}
        horizontal
        //
        dataTestId="navigation-menu-desktop"
      />

      {/* Delivery Buttons */}
      <div className={styles.deliverySection}>
        { !hideDeliveryDetails && <DeliveryButtons /> }
        { !hideCart && <HeaderCartButton hideCartOnLargeScreens={hideCartOnLargeScreens} /> }
      </div>

    </Bar>
  );
};

// --- Bar

interface HeaderBarProps {
  className?: string,
  children?: React.ReactNode
}

const Bar = (props: HeaderBarProps) => {
  const { className, children } = props;
  return (
    <div className={classnames(styles.bar, className)}>
      <div className={classnames(styles.container)}>
        {children}
      </div>
    </div>
  );
};

// Export

export default React.memo(HeaderDesktop);
